import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { deckService } from '../services/deckService';
import copyService from '../services/copyService';
import { extractIdFromSlug } from '../utils/slugUtils';
import toast from 'react-hot-toast';

export const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const token = searchParams.get('token');
        const refresh = searchParams.get('refresh');
        const error = searchParams.get('error');

        if (error) {
          toast.error('Falha na autenticação. Tente novamente.');
          navigate('/login');
          return;
        }

        if (!token || !refresh) {
          toast.error('Resposta de autenticação inválida. Tente novamente.');
          navigate('/login');
          return;
        }

        // Store tokens
        authService.setTokens(token, refresh);

        // Get user profile
        const user = await authService.getProfile();
        updateUser(user);

        toast.success('Bem-vindo! Você foi autenticado com sucesso.');
        
        // Check if there's a redirect URL (for deck copying)
        const oauthRedirect = localStorage.getItem('oauthRedirect');
        const intendedPath = localStorage.getItem('intendedPath');
        const redirectTo = oauthRedirect || intendedPath;
        
        if (redirectTo && redirectTo.startsWith('/study/')) {
          // Extract deck ID from the redirect URL
          const deckSlug = redirectTo.split('/study/')[1];
          const deckId = extractIdFromSlug(deckSlug);
          
          try {
            // Copy the deck to user's collection
            const copiedDeck = await deckService.copyDeck(deckId);
            
            toast.success(`Deck "${copiedDeck.name}" copiado com sucesso!`);
            
            // Clean up redirect from localStorage
            localStorage.removeItem('oauthRedirect');
            localStorage.removeItem('intendedPath');
            
            // Redirect to study the copied deck
            navigate(`/study/${copiedDeck._id}`);
            return;
          } catch (error: any) {
            console.error('Error copying deck:', error);
            
            // Handle specific error cases
            if (error.response?.data?.code === 'DECK_LIMIT_REACHED') {
              toast.error('Limite de decks atingido. Faça upgrade para Premium.');
            } else if (error.response?.data?.code === 'COPY_CARD_LIMIT_REACHED') {
              toast.error('Deck tem muitos cards para usuários gratuitos. Faça upgrade para Premium.');
            } else if (error.response?.status === 404) {
              toast.error('Deck não encontrado ou não está mais disponível.');
            } else {
              toast.error('Erro ao copiar deck. Tente novamente.');
            }
            
            // Clean up and fallback to home page
            localStorage.removeItem('oauthRedirect');
            localStorage.removeItem('intendedPath');
            navigate('/home');
            return;
          }
        } else if (redirectTo && redirectTo.startsWith('/quizzes/management/')) {
          // Handle quiz copying
          const quizSlug = redirectTo.split('/quizzes/management/')[1];
          try {
            const id = extractIdFromSlug(quizSlug);
            const result = await copyService.copyQuiz(id);
            toast.success(result.message);
            
            // Clean up redirect from localStorage
            localStorage.removeItem('oauthRedirect');
            localStorage.removeItem('intendedPath');
            
            navigate('/quizzes');
            return;
          } catch (error: any) {
            console.error('Error copying quiz:', error);
            toast.error(error.response?.data?.message || 'Erro ao copiar quiz');
            
            // Clean up and fallback to home page
            localStorage.removeItem('oauthRedirect');
            localStorage.removeItem('intendedPath');
            navigate('/home');
            return;
          }
        } else if (redirectTo && redirectTo.includes('/matches/')) {
          // Handle match copying (URLs like /matches/{slug}/play or /matches/{slug})
          const matchSlug = redirectTo.split('/matches/')[1]?.split('/')[0];
          if (matchSlug) {
            try {
              const id = extractIdFromSlug(matchSlug);
              const result = await copyService.copyMatch(id);
              toast.success(result.message);
              
              // Clean up redirect from localStorage
              localStorage.removeItem('oauthRedirect');
              localStorage.removeItem('intendedPath');
              
              navigate('/matches');
              return;
            } catch (error: any) {
              console.error('Error copying match:', error);
              toast.error(error.response?.data?.message || 'Erro ao copiar jogo');
              
              // Clean up and fallback to home page
              localStorage.removeItem('oauthRedirect');
              localStorage.removeItem('intendedPath');
              navigate('/home');
              return;
            }
          }
        }
        
        // Clean up redirect storage
        localStorage.removeItem('oauthRedirect');
        localStorage.removeItem('intendedPath');
        
        // Navigate to redirect path or default to home
        navigate(redirectTo || '/home');

      } catch (error: any) {
        console.error('OAuth callback error:', error);
        toast.error('Falha na autenticação. Tente novamente.');
        navigate('/login');
      } finally {
        setIsProcessing(false);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, updateUser]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
              Finalizando login...
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Aguarde enquanto completamos sua autenticação.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OAuthCallback;